import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../../store';
import { useToggleAutoSavingsQuery } from '../../../../hooks/query/useToggleAutoSavingsQuery';
import Button from '../../../../generic-components/Button';
import { AUTO_SAVING_DATA } from '../../constants';
import { useAccountSummaryQuery } from '../../../../hooks/query/useAccountSummaryQuery';

const EmergencyBucketAutoSaving = () => {
  const navigate = useNavigate();
  const { TITLE, A_DAY, TEXT_1, TEXT_2, BUTTON } = AUTO_SAVING_DATA;
  const summary = useStore((state) => state.summary);
  const [autoSavingOn, setAutoSavingOn] = useState<boolean>(summary.emergencyBucketResponse.autoSave);
  const { customerId } = useStore((state) => state.currUser());
  const { refetch: updateSummary } = useAccountSummaryQuery(() => {});
  const { refetch: toggleAutoSavings } = useToggleAutoSavingsQuery({
    customerId,
    isAutoSaving: !autoSavingOn,
    onSuccess: () => {
      setAutoSavingOn(!autoSavingOn);
    },
  });

  const getAutoSavingAmount = () => {
    if (summary.emergencyBucketResponse.amount) {
      return `$${summary.emergencyBucketResponse.amount}`;
    }
    return '';
  };

  useEffect(() => {
    updateSummary();
  }, [autoSavingOn]);

  const { t } = useTranslation();

  return (
    <div className='bg-bw00 h-[14.625rem] flex flex-col rounded-lg px-10 pt-[2.5rem]'>
      <div className='flex justify-between items-center'>
        <div id='autoSavingsTitle' className='text-base font-metricMedium text-bw08'>
          {t(TITLE)}
        </div>
        <Button
          id='toggleAutoSavings'
          childElement={
            <div
              className={`w-[1.125rem] h-[1.125rem] rounded-full ${autoSavingOn ? 'bg-action03 bg-opacity-100' : 'bg-bw06'}`}
            />
          }
          onClick={() => {
            if (summary.emergencyBucketResponse.active) toggleAutoSavings();
          }}
          className={`w-[3rem] h-[1.5rem] px-1 rounded-2xl flex items-center ${autoSavingOn ? 'flex-row-reverse bg-action02 bg-opacity-50' : 'flex-row bg-bw03'}`}
        />
      </div>
      <div
        className={`w-[23.5rem] mt-3 mb-6 opacity-30 border-t ${autoSavingOn ? 'border-bw06' : 'border-bw04 opacity-0'}`}
      />
      {!autoSavingOn && (
        <div className='absolute bg-bw04 w-[28.5rem] h-[9.85rem] rounded-b-lg mt-[2.25rem] ml-[-2.5rem] opacity-50 border-t border-bw03' />
      )}
      <div className='w-[23.5rem] h-full flex gap-[18px]'>
        <div className='flex flex-col text-action03'>
          <span id='autoSavingsBalanceGreen' className='text-[2.75rem] leading-[2.75rem] font-metricMedium'>
            {getAutoSavingAmount()}
          </span>
          <span id='aDay' className='mt-[0.313rem] text-sm font-metric'>
            {t(A_DAY)}
          </span>
        </div>
        <div className='flex flex-col'>
          <span
            className={`text-base font-metric text-bw07 ${getAutoSavingAmount().length === 4 ? 'whitespace-pre-line' : ''}`}
          >
            <span id='autoSavingsText1'>{t(TEXT_1)}</span>
            <span id='autoSavingsBalanceBlack' className='font-metricSemibold'>
              {getAutoSavingAmount()}
            </span>
            <span id='autoSavingsText2'>{t(TEXT_2)}</span>
          </span>
          <Button
            id='navigateStartSavings'
            disable={!autoSavingOn}
            onClick={() => {
              navigate('/emergency-bucket-start');
            }}
            className={`w-[13rem] h-[3.125rem] mt-[1.188rem] mr-[5.25rem] text-bw00 font-metricSemibold text-sm rounded-umd ${autoSavingOn ? 'bg-action03 hover:bg-action04' : 'bg-action02'}`}
            title={t(BUTTON)}
          />
        </div>
      </div>
    </div>
  );
};

export default EmergencyBucketAutoSaving;
